import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { MdPrint, MdRefresh, MdRemove, MdAdd } from "react-icons/md";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { brandFor } from "../businessBranding";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { useBranding } from "../theme/BrandingProvider";
import fallbackLogo from "../images/logo.png";
import "../work-orders-print.css";

/* ------------------------------------------------------------------ *
 * Delivery run sheet (print) — /run-sheet/print/:id
 *
 * Replaces the "runsheet" branch of pages/Print.js. Same URL (the run's
 * id), same endpoints: GET /run/getRun/:id for the run and GET
 * /settings/getAll for the easel / pedestal product types that add the
 * "Easels x N" / "Pedestals x N" notes to a stop's products.
 *
 * A run's `deliveries` holds one element per work order per product
 * group. As before, funeral elements are merged per funeral per
 * location and retail elements per work order, products pooled, so a
 * stop prints as one line. Columns are 1:1 with the legacy sheet.
 * ------------------------------------------------------------------ */

const PAPERS = [
  { id: "a4-landscape", label: "A4 landscape", page: "A4 landscape" },
  { id: "a3-landscape", label: "A3 landscape", page: "A3 landscape" },
  { id: "a4-portrait", label: "A4 portrait", page: "A4 portrait" },
];
const FONT_MIN = 9;
const FONT_MAX = 18;
const FONT_DEFAULT = 12;
const STORAGE_KEY = "rf-rsp-prefs";

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};
const locName = (raw) => (safeParse(raw, {}) || {}).name || "";
const locKey = (raw) => {
  const loc = safeParse(raw, {}) || {};
  const c = loc.coordinates || {};
  return c.lat && c.lng
    ? `${c.lat}_${c.lng}`
    : String(loc.name || "").toLowerCase().replace(/\s+/g, "");
};

function groupProducts(list) {
  const order = [];
  const byKey = {};
  (list || []).forEach((p) => {
    const type = String(p.product_type || "");
    const key =
      type.toLowerCase() === "casket cover"
        ? p.code || type
        : type || p.product_name || p.name || "Product";
    if (!byKey[key]) {
      byKey[key] = { key, amount: 0, isCC: type.toLowerCase() === "casket cover" };
      order.push(key);
    }
    byKey[key].amount += Number(p.amount) || 1;
  });
  return order.map((k) => byKey[k]);
}

/* "Easels x 3", "Pedestals x 2" — what the driver has to load besides
   the flowers. Types come from settings, as in the legacy sheet. */
function addOns(products, easelTypes, pedestalTypes) {
  let easels = 0;
  let pedestals = 0;
  (products || []).forEach((p) => {
    const n = Number(p.amount) || 0;
    if (pedestalTypes.includes(p.product_type)) pedestals += n;
    else if (easelTypes.includes(p.product_type)) easels += n;
  });
  const out = [];
  if (easels > 0) out.push({ key: "Easels", amount: easels });
  if (pedestals > 0) out.push({ key: "Pedestals", amount: pedestals });
  return out;
}

function buildStops(deliveries, easelTypes, pedestalTypes) {
  const order = [];
  const byKey = {};
  (deliveries || []).forEach((el) => {
    const key =
      el.order_type === "Funeral"
        ? `${el.parent_funeral_id}@${locKey(el.location)}`
        : String(el.work_order_id || el._id);
    if (!byKey[key]) {
      byKey[key] = { first: el, products: [], count: 0, serviceTime: "" };
      order.push(key);
    }
    byKey[key].products = byKey[key].products.concat(safeParse(el.products, []) || []);
    byKey[key].count += 1;
    /* any funeral delivery at the stop carries the service time */
    if (el.funeral_time && !byKey[key].serviceTime) byKey[key].serviceTime = el.funeral_time;
  });
  return order.map((key) => {
    const { first, products, count, serviceTime } = byKey[key];
    const funeral = first.order_type === "Funeral";
    return {
      id: key,
      funeral,
      recipient: funeral ? first.late_person_name || first.name || "" : first.name || "",
      fdCode: funeral ? (safeParse(first.fd_code, {}) || {}).code || "" : "N/A",
      location: locName(first.location),
      serviceTime: funeral ? serviceTime : "",
      time: first.time || "",
      products: groupProducts(products),
      addOns: addOns(products, easelTypes, pedestalTypes),
      elementCount: count,
    };
  });
}

function readPrefs() {
  try {
    return safeParse(localStorage.getItem(STORAGE_KEY), {}) || {};
  } catch (e) {
    return {};
  }
}
function writePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    /* private mode */
  }
}

function Chips({ items, extras }) {
  if ((!items || items.length === 0) && (!extras || extras.length === 0))
    return <span className="wop-empty">—</span>;
  return (
    <span className="wop-chips">
      {items.map((p) => (
        <span key={p.key} className={"wop-chip " + (p.isCC ? "is-cc" : "")}>
          <span className="wop-chip-name">{p.key}</span>
          <span className="wop-chip-n">×{p.amount}</span>
        </span>
      ))}
      {(extras || []).map((p) => (
        <span key={p.key} className="wop-chip is-addon">
          <span className="wop-chip-name">{p.key}</span>
          <span className="wop-chip-n">×{p.amount}</span>
        </span>
      ))}
    </span>
  );
}

const Cell = ({ children, className }) => (
  <td className={className}>
    {children === "" || children == null ? <span className="wop-empty">—</span> : children}
  </td>
);

export default function RunSheetPrintPage() {
  const { filter: runId } = useParams();
  const { business: tokenBusiness, user } = useAuth();
  /* Name, logo and palette follow the business that is open — the same
     one the shell and the :root theme use — falling back to the token's. */
  const activeBusiness = useActiveBusiness();
  const business =
    activeBusiness && (activeBusiness.name || activeBusiness.code)
      ? activeBusiness
      : tokenBusiness || activeBusiness;
  useBranding(business); // re-render when its logo or palette is saved

  const prefs = useMemo(readPrefs, []);
  const [font, setFont] = useState(() =>
    Math.min(FONT_MAX, Math.max(FONT_MIN, Number(prefs.font) || FONT_DEFAULT))
  );
  const [paper, setPaper] = useState(() =>
    PAPERS.some((p) => p.id === prefs.paper) ? prefs.paper : PAPERS[0].id
  );
  useEffect(() => {
    writePrefs({ font, paper });
  }, [font, paper]);

  const [run, setRun] = useState(null);
  const [types, setTypes] = useState({ easel: [], pedestal: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    axios
      .get(BASE_SERVER_URL + "/settings/getAll")
      .then((doc) => {
        if (doc.data.status === "success") {
          const s = doc.data.response[0] || {};
          setTypes({
            easel: safeParse(s.easel_types, []) || [],
            pedestal: safeParse(s.pedestal_types, []) || [],
          });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!runId) {
      setLoading(false);
      setError("This link has no run in it. Open the sheet from the run list.");
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError("");
    axios
      .get(BASE_SERVER_URL + "/run/getRun/" + runId)
      .then((doc) => {
        if (!alive) return;
        const found = doc.data.status === "success" && (doc.data.response || [])[0];
        if (found) setRun(found);
        else setError(doc.data.message || "No run sheet with this id was found.");
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Could not reach the server. Check your connection and try again."
        );
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [runId, reloadTick]);

  const stops = useMemo(
    () => buildStops((run || {}).deliveries, types.easel, types.pedestal),
    [run, types]
  );

  const day = moment((run || {}).delivery_date || "", "DD/MM/YYYY", true);
  const dateLong = day.isValid() ? day.format("dddd D MMMM YYYY") : (run || {}).delivery_date || "—";
  const dateShort = day.isValid() ? day.format("ddd D MMM YYYY") : (run || {}).delivery_date || "—";
  const brand = brandFor(business);
  const businessName = (business && business.name) || "Ray's Florist";
  const printedBy = (user && (user.name || user.full_name || user.username || user.email)) || "";
  const paperDef = PAPERS.find((p) => p.id === paper) || PAPERS[0];

  const productCount = stops.reduce(
    (n, s) => n + s.products.reduce((m, p) => m + p.amount, 0),
    0
  );
  const easelCount = stops.reduce(
    (n, s) => n + s.addOns.filter((a) => a.key === "Easels").reduce((m, a) => m + a.amount, 0),
    0
  );
  const pedestalCount = stops.reduce(
    (n, s) => n + s.addOns.filter((a) => a.key === "Pedestals").reduce((m, a) => m + a.amount, 0),
    0
  );
  const stats = [
    { k: "Stops", v: stops.length },
    { k: "Products", v: productCount },
    { k: "Easels", v: easelCount },
    { k: "Pedestals", v: pedestalCount },
  ];

  useEffect(() => {
    document.title = `Run sheet — ${(run || {}).drivers_name || ""} ${dateShort}`.trim();
  }, [run, dateShort]);

  const detail = run || {};
  const colCount = 6;

  return (
    <div className={"wop-page is-" + paper}>
      <style>{`@page { size: ${paperDef.page}; margin: 9mm 10mm; }`}</style>

      <header className="wop-toolbar no-print">
        <div className="wop-tb-title">
          <span className="wop-tb-kicker">Print · Delivery run sheet</span>
          <span className="wop-tb-date">
            {loading ? "Loading…" : detail.drivers_name ? `${detail.drivers_name} · ${dateShort}` : dateLong}
          </span>
        </div>

        <div className="wop-tb-stats">
          {stats.map((s) => (
            <span key={s.k} className="wop-stat">
              <span className="wop-stat-v">{loading ? "…" : s.v}</span>
              <span className="wop-stat-k">{s.k}</span>
            </span>
          ))}
        </div>

        <div className="wop-tb-controls">
          <label className="wop-ctrl">
            <span className="wop-ctrl-k">Text size</span>
            <span className="wop-stepper">
              <button
                type="button"
                aria-label="Smaller text"
                disabled={font <= FONT_MIN}
                onClick={() => setFont((f) => Math.max(FONT_MIN, f - 1))}
              >
                <MdRemove />
              </button>
              <span className="wop-stepper-v">{font}</span>
              <button
                type="button"
                aria-label="Larger text"
                disabled={font >= FONT_MAX}
                onClick={() => setFont((f) => Math.min(FONT_MAX, f + 1))}
              >
                <MdAdd />
              </button>
            </span>
          </label>

          <label className="wop-ctrl">
            <span className="wop-ctrl-k">Paper</span>
            <span className="wop-segmented" role="radiogroup" aria-label="Paper size">
              {PAPERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={paper === p.id}
                  className={paper === p.id ? "is-on" : ""}
                  onClick={() => setPaper(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </span>
          </label>

          <button
            type="button"
            className="wop-btn is-ghost"
            onClick={() => setReloadTick((t) => t + 1)}
            disabled={loading}
            title="Reload the run"
          >
            <MdRefresh /> Refresh
          </button>
          <button
            type="button"
            className="wop-btn is-primary"
            onClick={() => window.print()}
            disabled={loading || Boolean(error)}
          >
            <MdPrint /> Print
          </button>
        </div>
      </header>

      <main className="wop-sheet" style={{ "--wop-font": font + "px" }}>
        <div className="wop-head">
          <div className="wop-brand">
            <img src={brand.logo || fallbackLogo} alt={businessName} className="wop-logo" />
          </div>
          <div className="wop-head-mid">
            <h1 className="wop-title">Delivery Run Sheet</h1>
            <span className="wop-subtitle">{businessName}</span>
          </div>
          <div className="wop-head-date">
            <span className="wop-date-dow">{day.isValid() ? day.format("dddd") : ""}</span>
            <span className="wop-date-main">
              {day.isValid() ? day.format("D MMMM YYYY") : detail.delivery_date || "—"}
            </span>
          </div>
        </div>

        {/* run details — vehicle, driver, sign line, description, depart */}
        <div className="wop-run">
          <div className="wop-run-col">
            <div className="wop-run-field">
              <span className="wop-run-k">Vehicle no. plate</span>
              <span className="wop-run-v">{detail.vehicle_number || <span className="wop-empty">—</span>}</span>
            </div>
            <div className="wop-run-row">
              <div className="wop-run-field">
                <span className="wop-run-k">Driver's name</span>
                <span className="wop-run-v">{detail.drivers_name || <span className="wop-empty">—</span>}</span>
              </div>
              <div className="wop-run-field is-sign">
                <span className="wop-run-k">Driver sign</span>
                <span className="wop-run-line" />
              </div>
            </div>
          </div>
          <div className="wop-run-col">
            <div className="wop-run-field">
              <span className="wop-run-k">Description</span>
              <span className="wop-run-v">{detail.description || <span className="wop-empty">—</span>}</span>
            </div>
            <div className="wop-run-field">
              <span className="wop-run-k">Depart</span>
              <span className="wop-run-v">{detail.comment || <span className="wop-empty">—</span>}</span>
            </div>
          </div>
        </div>

        <div className="wop-summary">
          {stats.map((s) => (
            <span key={s.k} className="wop-summary-item">
              <span className="wop-summary-k">{s.k}</span>
              <span className="wop-summary-v">{loading ? "…" : s.v}</span>
            </span>
          ))}
        </div>

        <table className="wop-table">
          <colgroup>
            <col className="wop-c-num" />
            <col style={{ width: "19%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "26%" }} />
          </colgroup>
          <thead>
            <tr>
              <th className="wop-num">#</th>
              <th>Recipient</th>
              <th>FD code</th>
              <th>Delivery location</th>
              <th className="wop-center">Delivery time</th>
              <th>Products</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [0, 1, 2, 3, 4].map((i) => (
                <tr key={i} className="wop-skeleton">
                  <td colSpan={colCount}>
                    <span className="wop-skel" />
                  </td>
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={colCount} className="wop-state is-error">
                  <strong>Nothing to print.</strong> {error}
                  {runId && (
                    <button
                      type="button"
                      className="wop-btn is-ghost no-print"
                      onClick={() => setReloadTick((t) => t + 1)}
                    >
                      <MdRefresh /> Try again
                    </button>
                  )}
                </td>
              </tr>
            ) : stops.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="wop-state">
                  This run has no deliveries yet.
                </td>
              </tr>
            ) : (
              stops.map((s, i) => (
                <tr key={s.id}>
                  <td className="wop-num">{i + 1}</td>
                  <td className="wop-strong">
                    {s.recipient || <span className="wop-empty">—</span>}
                    <span className="wop-sub">{s.funeral ? "Funeral" : "Retail"}</span>
                  </td>
                  <Cell>{s.fdCode}</Cell>
                  <Cell>
                    {s.location}
                    {s.serviceTime && (
                      <span className="wop-service">
                        {" "}- Funeral Service Time <b>{s.serviceTime}</b>
                      </span>
                    )}
                  </Cell>
                  <Cell className="wop-center wop-time">{s.time}</Cell>
                  <td>
                    <Chips items={s.products} extras={s.addOns} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <footer className="wop-foot">
          <span>
            {businessName} · Delivery run sheet · {dateShort}
            {detail.drivers_name ? ` · ${detail.drivers_name}` : ""}
          </span>
          <span>
            Printed {moment().format("D MMM YYYY, h:mm a")}
            {printedBy ? ` by ${printedBy}` : ""}
          </span>
        </footer>
      </main>
    </div>
  );
}
